import { Fragment, useCallback, useEffect, useState } from "react";
import { json, Form, useParams } from "react-router-dom";
import TestResults from "./TestResults.js";
import LoadingPlaceholder from "../../../components/ui/loading/LoadingPlaceholder";
import {
  fetchCurrentQuestion,
  processTestAndGetNextQuestion,
} from "../../../api/OnboardCpa/fetchTest.js";
import { Handler401 } from "../../../api/utils/responseActions.js";
import { useRefreshOnAuth } from "../../../hooks/useRefreshOnAuth.js";

const CountdownTimer = ({ initialSeconds, seconds, setSeconds }) => {
  useEffect(() => {
    if (initialSeconds > 0) {
      const intervalId = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [initialSeconds]);

  return (
    <div className="timer" style={{ width: "340px" }}>
      {seconds > 0 ? (
        <h1>Time Remaining: {formatTime(seconds)}</h1>
      ) : (
        <h1>Time's up!</h1>
      )}
    </div>
  );
};

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes < 10 ? "0" : ""}${minutes}:${
    remainingSeconds < 10 ? "0" : ""
  }${remainingSeconds}`;
};

const CpaTestQuestion = () => {
  let { testId } = useParams();
  const [testData, setTestData] = useState({});
  const [loadingTest, setLoadingTest] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(-1);
  const [seconds, setSeconds] = useState(0);

  const getCurrentQuestion = useCallback(async () => {
    setLoadingTest(true);
    let endpoint = "utils/cpa-test/" + testId;

    try {
      const response = await fetchCurrentQuestion(endpoint);

      if (!response.ok) {
        if (response.status === 401) {
          return Handler401();
        }
        // Use standard error handling instead of throwing JSON in a component body
        console.error("Fetch failed", response.status);
        return;
      }

      const data = await response.json();
      setTestData(data);
      setSeconds(data["remaining_time"]);
      setLoadingTest(false);
    } catch (err) {
      console.log(err);
      setLoadingTest(false);
    }
  }, [testId]);

  // This will reload the data on session restore without unmount/mount the components
  useRefreshOnAuth(getCurrentQuestion);

  async function getNextQuestion() {
    setLoadingTest(true);

    // Time in millisecond when the answer was submitted
    const currentTime = new Date();
    const currentTimeMilliseconds = currentTime.getTime();

    // API endpoint
    //let url = `${config.api.cpa-test}${testId}`;
    let endpoint = "utils/cpa-test/" + testId;

    // Build the request Object
    let queryData = {};
    queryData.submitted_time = currentTimeMilliseconds;
    if (selectedAnswer !== -1) {
      queryData.selected_answer = selectedAnswer;
      queryData.question_id = testData.question.id;
    }

    // Fetch data from the API
    const response = await processTestAndGetNextQuestion(endpoint, queryData);

    if (!response.ok) {
      if (response.status === 401) {
        return Handler401();
      }
      throw json({ status: response.status, message: response.message });
    }
    const data = await response.json();
    setSeconds(data["remaining_time"]);
    setTestData(data);
    setLoadingTest(false);
  }

  const submitQuestionHandler = useCallback((event) => {
    event.preventDefault();
    getNextQuestion();
  });

  const checkQuestionChanged = (event) => {
    setSelectedAnswer(event.target.value);
  };

  // This side effect is used for: (a) Refresh page (b) When render the page for first
  useEffect(() => {
    getCurrentQuestion();
  }, []);

  return (
    <div className="main-content-area">
      <div className="content">
        {loadingTest ? (
          <div className="internal-content-body">
            <LoadingPlaceholder rows={1} type="card-large" />
          </div>
        ) : (
          <Fragment>
            {testData.testStatus === 2 && (
              <TestResults
                result={testData.passed}
                message={testData.message}
                attempts={testData.attemptsLeft}
              />
            )}
            {testData.testStatus === 1 && (
              <div>
                <Form
                  method="put"
                  className="carousel2"
                  onSubmit={submitQuestionHandler}
                >
                  <input
                    type="radio"
                    name="carousel2"
                    id="carousel2card1"
                    checked
                  />
                  <div className="questionCard">
                    <div>
                      <CountdownTimer
                        initialSeconds={seconds}
                        seconds={seconds}
                        setSeconds={setSeconds}
                      />
                      <div className="checkbox">
                        <h3>{testData.question.description}</h3>

                        {testData.answers.map((answer, index) => {
                          return (
                            <Fragment>
                              <input
                                type="checkbox"
                                name="answer"
                                value={`${answer.id}`}
                                id={`q4checkbox${index + 1}`}
                                checked={selectedAnswer === `${answer.id}`}
                                onChange={checkQuestionChanged}
                              />
                              <label htmlFor={`q4checkbox${index + 1}`}>
                                {answer.description}
                              </label>
                            </Fragment>
                          );
                        })}
                      </div>
                      <button
                        className="cta-button primary"
                        data-icon="arrow_forward"
                      >
                        <b>Submit</b>
                      </button>
                    </div>
                  </div>
                </Form>
              </div>
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default CpaTestQuestion;
